// frontend/src/components/Dashboard/TestimonialManager.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  getUserTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  getAllTestimonials,
  getTestimonialStats,
  approveTestimonial,
  rejectTestimonial,
  featureTestimonial,
  adminRespondToTestimonial,
  markTestimonialHelpful,
} from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faStarHalfAlt,
  faEdit,
  faTrash,
  faPlus,
  faTimes,
  faCheck,
  faQuoteLeft,
  faUser,
  faTractor,
  faStore,
  faThumbsUp,
  faThumbsDown,
  faReply,
  faThumbtack,
  faClock,
  faSpinner,
  faFilter,
  faSearch,
  faExclamationTriangle,
  faCheckCircle,
  faEye,
  faEyeSlash,
  faSort,
  faSortUp,
  faSortDown,
  faCalendarAlt,
  faUserCircle,
  faEnvelope,
  faPhone,
  faBox,
  faTag,
  faInfoCircle,
  faReplyAll,
  faWhatsapp,
  faGlobe,
  faShip,
  faAnchor,
  faBuilding,
  faLeaf,
  faCow,
  faRocket,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../UI/Loader";
import RatingStar from "../UI/RatingStar";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusBadge = (status) => {
  const styles = {
    pending: {
      bg: "#fef3c7",
      color: "#d97706",
      text: "⏳ Pending Review",
      icon: faClock,
    },
    approved: {
      bg: "#dcfce7",
      color: "#16a34a",
      text: "✅ Approved",
      icon: faCheck,
    },
    rejected: {
      bg: "#fee2e2",
      color: "#dc2626",
      text: "❌ Rejected",
      icon: faTimes,
    },
  };
  return styles[status] || styles.pending;
};

// ============================================
// MAIN COMPONENT
// ============================================
const TestimonialManager = ({ isAdmin = true }) => {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [formData, setFormData] = useState({
    rating: 5,
    title: "",
    content: "",
    productName: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [viewMode, setViewMode] = useState("grid");

  // ============================================
  // FETCH TESTIMONIALS
  // ============================================
  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    try {
      let testimonialsData, statsData;

      if (isAdmin) {
        const [testimonialsRes, statsRes] = await Promise.all([
          getAllTestimonials({ status: filter, search: searchTerm }),
          getTestimonialStats(),
        ]);
        testimonialsData =
          testimonialsRes?.data?.testimonials || testimonialsRes?.data || [];
        statsData = statsRes?.data || {};
      } else {
        const [testimonialsRes, statsRes] = await Promise.all([
          getUserTestimonials(),
          getTestimonialStats
            ? getTestimonialStats()
            : Promise.resolve({ data: {} }),
        ]);
        testimonialsData =
          testimonialsRes?.data?.testimonials || testimonialsRes?.data || [];
        statsData = statsRes?.data || {};
      }

      setTestimonials(Array.isArray(testimonialsData) ? testimonialsData : []);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      setTestimonials([]);
      setMessage({ type: "error", text: "Failed to load testimonials" });
    } finally {
      setLoading(false);
    }
  }, [filter, searchTerm, isAdmin]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      if (editingTestimonial) {
        await updateTestimonial(editingTestimonial._id, formData);
        setMessage({
          type: "success",
          text: "Testimonial updated successfully!",
        });
      } else {
        await createTestimonial(formData);
        setMessage({
          type: "success",
          text: "Thank you! Your testimonial will be reviewed.",
        });
      }
      setShowModal(false);
      resetForm();
      fetchTestimonials();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to save testimonial",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimonial?")) {
      try {
        await deleteTestimonial(id);
        fetchTestimonials();
        setMessage({
          type: "success",
          text: "Testimonial deleted successfully",
        });
      } catch (error) {
        setMessage({ type: "error", text: "Failed to delete testimonial" });
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveTestimonial(id);
      fetchTestimonials();
      setMessage({ type: "success", text: "Testimonial approved!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to approve testimonial" });
    }
  };

  const handleReject = async (id) => {
    const reason = prompt("Please provide a reason for rejection:");
    if (reason !== null) {
      try {
        await rejectTestimonial(id, { rejectionReason: reason });
        fetchTestimonials();
        setMessage({ type: "success", text: "Testimonial rejected" });
      } catch (error) {
        setMessage({ type: "error", text: "Failed to reject testimonial" });
      }
    }
  };

  const handleFeature = async (id) => {
    try {
      await featureTestimonial(id);
      fetchTestimonials();
      setMessage({ type: "success", text: "Testimonial featured!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to feature testimonial" });
    }
  };

  const handleAdminResponse = async () => {
    if (!responseText.trim()) {
      setMessage({ type: "error", text: "Please enter a response" });
      return;
    }
    try {
      await adminRespondToTestimonial(selectedTestimonial._id, responseText);
      setShowResponseModal(false);
      setResponseText("");
      setSelectedTestimonial(null);
      fetchTestimonials();
      setMessage({ type: "success", text: "Response sent successfully!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to send response" });
    }
  };

  const handleMarkHelpful = async (id) => {
    try {
      await markTestimonialHelpful(id);
      fetchTestimonials();
    } catch (error) {
      setMessage({ type: "error", text: "Failed to mark as helpful" });
    }
  };

  const resetForm = () => {
    setFormData({ rating: 5, title: "", content: "", productName: "" });
    setEditingTestimonial(null);
  };

  const openEditModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      rating: testimonial.rating,
      title: testimonial.title,
      content: testimonial.content,
      productName: testimonial.productName || "",
    });
    setShowModal(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDirection === "asc" ? faSortUp : faSortDown;
  };

  const getStatusStats = () => {
    return [
      { label: "Total", value: stats.total || 0, color: "#3b82f6" },
      { label: "Pending", value: stats.pending || 0, color: "#f59e0b" },
      { label: "Approved", value: stats.approved || 0, color: "#16a34a" },
      { label: "Rejected", value: stats.rejected || 0, color: "#dc2626" },
      { label: "Featured", value: stats.featured || 0, color: "#8b5cf6" },
    ];
  };

  const sortedTestimonials = [...testimonials].sort((a, b) => {
    let aVal = a[sortField] || "";
    let bVal = b[sortField] || "";
    if (sortField === "rating" || sortField === "helpfulCount") {
      aVal = aVal || 0;
      bVal = bVal || 0;
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    }
    if (typeof aVal === "string") {
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
  });

  const userTypeIcon = user?.userType === "farmer" ? faTractor : faStore;

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  // Admin View
  if (isAdmin) {
    return (
      <div className="testimonial-manager admin">
        {/* Header */}
        <div className="section-header">
          <div className="header-left">
            <FontAwesomeIcon icon={faQuoteLeft} className="header-icon" />
            <h2>Testimonial Management</h2>
            <span className="supplier-badge">
              <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
            </span>
          </div>
          <div className="header-actions">
            <button
              className="btn-toggle-view"
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
            >
              <FontAwesomeIcon
                icon={viewMode === "grid" ? faEyeSlash : faEye}
              />
              {viewMode === "grid" ? "List View" : "Grid View"}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="admin-stats-grid">
          {getStatusStats().map((stat, index) => (
            <div
              key={index}
              className="stat-item-card"
              style={{ borderColor: stat.color }}
            >
              <div className="stat-value" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
          {stats.averageRating > 0 && (
            <div className="stat-item-card rating">
              <div className="stat-value">
                {stats.averageRating.toFixed(1)}
                <span className="stat-star">⭐</span>
              </div>
              <div className="stat-label">Avg Rating</div>
            </div>
          )}
          <div className="stat-item-card malaysia">
            <div className="stat-value">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div className="stat-label">{SUPPLIER.country} Sourced</div>
          </div>
        </div>

        {/* Supplier Info */}
        <div className="supplier-info-card">
          <FontAwesomeIcon icon={faShip} className="supplier-icon" />
          <div className="supplier-info">
            <strong>Products sourced from {SUPPLIER.name}</strong>
            <span>
              <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} &bull;
              <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
            </span>
          </div>
          <span className="supplier-badge">
            <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
          </span>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <div className="filters-left">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Testimonials</option>
              <option value="pending">⏳ Pending</option>
              <option value="approved">✅ Approved</option>
              <option value="rejected">❌ Rejected</option>
            </select>
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Search testimonials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          <div className="filters-right">
            <button
              className="btn-sort"
              onClick={() => handleSort("createdAt")}
            >
              Date <FontAwesomeIcon icon={getSortIcon("createdAt")} />
            </button>
            <button className="btn-sort" onClick={() => handleSort("rating")}>
              Rating <FontAwesomeIcon icon={getSortIcon("rating")} />
            </button>
            <button
              className="btn-sort"
              onClick={() => handleSort("helpfulCount")}
            >
              Helpful <FontAwesomeIcon icon={getSortIcon("helpfulCount")} />
            </button>
          </div>
        </div>

        {/* Messages */}
        {message.text && (
          <div className={`alert-message ${message.type}`}>
            <FontAwesomeIcon
              icon={
                message.type === "success"
                  ? faCheckCircle
                  : faExclamationTriangle
              }
            />
            {message.text}
          </div>
        )}

        {/* Testimonials List */}
        <div className={`testimonials-list ${viewMode}`}>
          {sortedTestimonials.length === 0 ? (
            <div className="empty-testimonials">
              <FontAwesomeIcon icon={faQuoteLeft} size="3x" />
              <h3>No Testimonials Found</h3>
              <p>Testimonials from users will appear here for moderation.</p>
            </div>
          ) : (
            sortedTestimonials.map((testimonial) => {
              const statusStyle = getStatusBadge(testimonial.status);
              const isMalaysia = testimonial.origin === SUPPLIER.country;

              return (
                <div
                  key={testimonial._id}
                  className={`testimonial-card ${testimonial.isFeatured ? "featured" : ""}`}
                >
                  <div className="testimonial-header">
                    <div className="testimonial-user">
                      <div className="user-avatar">
                        {testimonial.userAvatar ? (
                          <img
                            src={testimonial.userAvatar}
                            alt={testimonial.userName}
                          />
                        ) : (
                          <FontAwesomeIcon icon={faUser} />
                        )}
                      </div>
                      <div>
                        <div className="user-name">
                          {testimonial.userName}
                          {isMalaysia && (
                            <span className="origin-tag">
                              <FontAwesomeIcon icon={faGlobe} />{" "}
                              {SUPPLIER.country}
                            </span>
                          )}
                        </div>
                        <div className="user-email">
                          <FontAwesomeIcon icon={faEnvelope} />{" "}
                          {testimonial.userEmail}
                        </div>
                        <div className="user-type">
                          {testimonial.userType === "farmer" ? (
                            <>
                              <FontAwesomeIcon icon={faTractor} /> Farmer
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faStore} /> Customer
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="testimonial-actions-admin">
                      <span
                        className="testimonial-status"
                        style={{
                          background: statusStyle.bg,
                          color: statusStyle.color,
                        }}
                      >
                        <FontAwesomeIcon icon={statusStyle.icon} />{" "}
                        {statusStyle.text}
                      </span>
                      <div className="action-buttons">
                        {testimonial.status === "pending" && (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => handleApprove(testimonial._id)}
                              title="Approve"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => handleReject(testimonial._id)}
                              title="Reject"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </>
                        )}
                        <button
                          className={`btn-feature ${testimonial.isFeatured ? "active" : ""}`}
                          onClick={() => handleFeature(testimonial._id)}
                          title={
                            testimonial.isFeatured ? "Unfeature" : "Feature"
                          }
                        >
                          <FontAwesomeIcon icon={faThumbtack} />
                        </button>
                        <button
                          className="btn-respond"
                          onClick={() => {
                            setSelectedTestimonial(testimonial);
                            setResponseText(testimonial.adminResponse || "");
                            setShowResponseModal(true);
                          }}
                          title="Respond"
                        >
                          <FontAwesomeIcon icon={faReply} />
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(testimonial._id)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="testimonial-body">
                    <div className="testimonial-rating">
                      <RatingStar rating={testimonial.rating} />
                      <span className="rating-date">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {new Date(testimonial.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    <h3 className="testimonial-title">{testimonial.title}</h3>
                    <p className="testimonial-content">{testimonial.content}</p>

                    {testimonial.productName && (
                      <div className="testimonial-product">
                        <FontAwesomeIcon icon={faTag} />
                        Product: {testimonial.productName}
                        {isMalaysia && (
                          <span className="origin-tag">
                            <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                          </span>
                        )}
                      </div>
                    )}

                    {testimonial.images && testimonial.images.length > 0 && (
                      <div className="testimonial-images">
                        {testimonial.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Testimonial ${idx + 1}`}
                          />
                        ))}
                      </div>
                    )}

                    {testimonial.adminResponse && (
                      <div className="testimonial-response">
                        <div className="response-label">
                          <FontAwesomeIcon icon={faReplyAll} /> Admin Response:
                        </div>
                        <p>{testimonial.adminResponse}</p>
                        {testimonial.responseDate && (
                          <div className="response-date">
                            {new Date(
                              testimonial.responseDate,
                            ).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="testimonial-footer">
                    <div className="testimonial-meta">
                      <button
                        className="helpful-btn"
                        onClick={() => handleMarkHelpful(testimonial._id)}
                      >
                        <FontAwesomeIcon icon={faThumbsUp} />
                        <span>{testimonial.helpfulCount || 0}</span>
                      </button>
                      {testimonial.isFeatured && (
                        <span className="featured-badge">
                          <FontAwesomeIcon icon={faThumbtack} /> Featured
                        </span>
                      )}
                      {isMalaysia && (
                        <span className="origin-badge">
                          <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Response Modal */}
        {showResponseModal && selectedTestimonial && (
          <div
            className="modal-overlay"
            onClick={() => setShowResponseModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={faReplyAll} /> Respond to Testimonial
                </h2>
                <button
                  className="close-modal"
                  onClick={() => setShowResponseModal(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body">
                <div className="testimonial-preview">
                  <div className="preview-header">
                    <strong>From:</strong> {selectedTestimonial.userName}
                    <span className="preview-type">
                      {selectedTestimonial.userType === "farmer"
                        ? "🌾 Farmer"
                        : "🛒 Customer"}
                    </span>
                    {selectedTestimonial.origin === SUPPLIER.country && (
                      <span className="origin-tag">
                        <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                      </span>
                    )}
                  </div>
                  <div className="preview-content">
                    <strong>Testimonial:</strong>
                    <p>{selectedTestimonial.content}</p>
                  </div>
                  {selectedTestimonial.adminResponse && (
                    <div className="previous-response">
                      <strong>Previous Response:</strong>
                      <p>{selectedTestimonial.adminResponse}</p>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Response *</label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows="4"
                    placeholder="Write your response to this testimonial..."
                    className="response-textarea"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setShowResponseModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleAdminResponse}
                  disabled={!responseText.trim()}
                >
                  <FontAwesomeIcon icon={faReplyAll} /> Send Response
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // User View (Customer/Farmer)
  return (
    <div className="testimonial-manager user">
      <div className="section-header">
        <div className="header-left">
          <FontAwesomeIcon icon={faQuoteLeft} className="header-icon" />
          <h2>My Testimonials</h2>
          <span className="supplier-badge">
            <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
          </span>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Share Your Experience
        </button>
      </div>

      {/* User Stats */}
      <div className="user-stats-mini">
        <div className="stat-mini">
          <span className="stat-mini-value">{testimonials.length}</span>
          <span className="stat-mini-label">Total</span>
        </div>
        <div className="stat-mini">
          <span className="stat-mini-value">
            {testimonials.filter((t) => t.status === "approved").length}
          </span>
          <span className="stat-mini-label">Approved</span>
        </div>
        <div className="stat-mini">
          <span className="stat-mini-value">
            {testimonials.filter((t) => t.status === "pending").length}
          </span>
          <span className="stat-mini-label">Pending</span>
        </div>
        <div className="stat-mini">
          <span className="stat-mini-value">
            {testimonials.filter((t) => t.status === "rejected").length}
          </span>
          <span className="stat-mini-label">Rejected</span>
        </div>
        <div className="stat-mini malaysia">
          <span className="stat-mini-value">
            <FontAwesomeIcon icon={faGlobe} />
          </span>
          <span className="stat-mini-label">{SUPPLIER.country} Sourced</span>
        </div>
      </div>

      {message.text && (
        <div className={`alert-message ${message.type}`}>
          <FontAwesomeIcon
            icon={
              message.type === "success" ? faCheckCircle : faExclamationTriangle
            }
          />
          {message.text}
        </div>
      )}

      {testimonials.length === 0 ? (
        <div className="empty-testimonials">
          <FontAwesomeIcon icon={faQuoteLeft} size="3x" />
          <h3>No Testimonials Yet</h3>
          <p>
            Share your experience with our products sourced from {SUPPLIER.name}{" "}
            ({SUPPLIER.country}) and help other farmers and customers make
            informed decisions.
          </p>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faPlus} /> Write Your First Testimonial
          </button>
        </div>
      ) : (
        <div className="testimonials-list user-list">
          {testimonials.map((testimonial) => {
            const statusStyle = getStatusBadge(testimonial.status);
            const isMalaysia = testimonial.origin === SUPPLIER.country;

            return (
              <div
                key={testimonial._id}
                className={`testimonial-card ${testimonial.status}`}
              >
                <div className="testimonial-header">
                  <div className="testimonial-user">
                    <div className="user-avatar">
                      <FontAwesomeIcon icon={userTypeIcon} />
                    </div>
                    <div>
                      <div className="user-name">
                        {testimonial.userName || user?.name}
                        {isMalaysia && (
                          <span className="origin-tag">
                            <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                          </span>
                        )}
                      </div>
                      <div className="user-type">
                        {user?.userType === "farmer" ? (
                          <>
                            <FontAwesomeIcon icon={faTractor} /> Farmer
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faStore} /> Customer
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <span
                    className="testimonial-status"
                    style={{
                      background: statusStyle.bg,
                      color: statusStyle.color,
                    }}
                  >
                    <FontAwesomeIcon icon={statusStyle.icon} />{" "}
                    {statusStyle.text}
                  </span>
                </div>

                <div className="testimonial-body">
                  <div className="testimonial-rating">
                    <RatingStar rating={testimonial.rating} />
                    <span className="rating-date">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {new Date(testimonial.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <h3 className="testimonial-title">{testimonial.title}</h3>
                  <p className="testimonial-content">{testimonial.content}</p>

                  {testimonial.productName && (
                    <div className="testimonial-product">
                      <FontAwesomeIcon icon={faTag} />
                      Product: {testimonial.productName}
                      {isMalaysia && (
                        <span className="origin-tag">
                          <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                        </span>
                      )}
                    </div>
                  )}

                  {testimonial.status === "rejected" &&
                    testimonial.adminResponse && (
                      <div className="testimonial-feedback rejected">
                        <div className="feedback-label">
                          <FontAwesomeIcon icon={faExclamationTriangle} /> Admin
                          Feedback:
                        </div>
                        <p>{testimonial.adminResponse}</p>
                      </div>
                    )}

                  {testimonial.status === "approved" &&
                    testimonial.adminResponse && (
                      <div className="testimonial-response">
                        <div className="response-label">
                          <FontAwesomeIcon icon={faReplyAll} /> Response from
                          Kenali Enterprise:
                        </div>
                        <p>{testimonial.adminResponse}</p>
                      </div>
                    )}
                </div>

                <div className="testimonial-footer">
                  <div className="testimonial-actions">
                    {testimonial.status === "pending" && (
                      <>
                        <button
                          className="btn-edit"
                          onClick={() => openEditModal(testimonial)}
                        >
                          <FontAwesomeIcon icon={faEdit} /> Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(testimonial._id)}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                  <div className="testimonial-helpful">
                    <FontAwesomeIcon icon={faThumbsUp} />
                    <span>{testimonial.helpfulCount || 0}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingTestimonial ? (
                  <>
                    <FontAwesomeIcon icon={faEdit} /> Edit Your Testimonial
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} /> Share Your Experience
                  </>
                )}
                <span className="supplier-badge">
                  <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                </span>
              </h2>
              <button
                className="close-modal"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="testimonial-form">
              <div className="form-group">
                <label>Rating *</label>
                <div className="rating-input">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`rating-star ${formData.rating >= star ? "active" : ""}`}
                      onClick={() => setFormData({ ...formData, rating: star })}
                    >
                      <FontAwesomeIcon icon={faStar} />
                    </button>
                  ))}
                  <span className="rating-label">{formData.rating} / 5</span>
                </div>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  placeholder="e.g., Excellent product, highly recommend!"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                  maxLength="100"
                  className="form-input"
                />
                <small className="char-count">
                  {formData.title.length}/100
                </small>
              </div>

              <div className="form-group">
                <label>Your Experience *</label>
                <textarea
                  placeholder="Share your experience with our products sourced from Malaysia. What did you like? How did it help you?"
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  required
                  rows="5"
                  maxLength="1000"
                  className="form-textarea"
                />
                <small className="char-count">
                  {formData.content.length}/1000
                </small>
              </div>

              <div className="form-group">
                <label>Product Name (Optional)</label>
                <input
                  type="text"
                  placeholder="Which product are you reviewing?"
                  value={formData.productName}
                  onChange={(e) =>
                    setFormData({ ...formData, productName: e.target.value })
                  }
                  className="form-input"
                />
              </div>

              <div className="supplier-note">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  All products are sourced from <strong>{SUPPLIER.name}</strong>{" "}
                  in <strong>{SUPPLIER.country}</strong>, ensuring premium
                  quality.
                </span>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
                    </>
                  ) : editingTestimonial ? (
                    <>
                      <FontAwesomeIcon icon={faEdit} /> Update
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} /> Submit Testimonial
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="modal-footer-note">
              <FontAwesomeIcon icon={faInfoCircle} />
              <small>
                Your testimonial will be reviewed before being published. Thank
                you for sharing!
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialManager;
